import { __extends } from "tslib";
import { MarkerType_$type } from "./MarkerType";
import { MarkerOutlineMode_$type } from "./MarkerOutlineMode";
import { MarkerFillMode_$type } from "./MarkerFillMode";
import { IgrSeries } from "./igr-series";
import { ensureEnum, brushToString, stringToBrush } from "igniteui-react-core";
/**
 * Represents the base class for series containing markers.
*
* MarkerSeries represents the base class for series containing markers.
*
* Example:
*
* ```ts
*  <IgrDataChart
*     dataSource={this.state.dataSource} >
*
*     <IgrCategoryXAxis name="xAxis" />
*     <IgrNumericYAxis name="yAxis" />
*
*     <IgrColumnSeries
*      name="series1"
*     xAxisName="xAxis"
* 	yAxisName="yAxis"
* 	valueMemberPath="Value" />
*  </IgrDataChart>
* ```
*
* ```ts
* this.series = new IgrMarkerSeries({name: "series"});
* this.series.xAxis = this.categoryXAxis;
* this.series.yAxis = this.numericYAxis;
* this.series.xAxisName = "timeXAxis";
* this.series.yAxisName = "numericYAxis";
* this.series.volumeMemberPath="Volume";
* ```
*/
var IgrMarkerSeries = /** @class */ /*@__PURE__*/ (function (_super) {
    __extends(IgrMarkerSeries, _super);
    function IgrMarkerSeries(props) {
        return _super.call(this, props) || this;
    }
    Object.defineProperty(IgrMarkerSeries.prototype, "i", {
        /**
                                     * @hidden
                                     */
        get: function () {
            return this._implementation;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IgrMarkerSeries.prototype, "hasMarkers", {
        /**
         * Returns whether the current series supports visual markers.
        *
        * `HasMarkers` property is overriden, it returns whether the current series supports visual markers. You can use it like this:
        *
        * ```ts
        *  let gotMarkers: boolean = this.series.hasMarkers;
        * ```
        */
        get: function () {
            return this.i.hasMarkers;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IgrMarkerSeries.prototype, "markerType", {
        /**
         * Gets or sets the marker type for the current series object.
         * This property is ignored when the MarkerTemplate property is set
        *
        * `MarkerType` property gets or sets the marker type for the current series object. For example, you can set it like this:
        *
        * ```ts
        * this.series.markerType = "circle";
        * ```
        *
        * ```ts
        *  <IgrDataChart
        *     dataSource={this.state.dataSource} >
        *
        *     <IgrCategoryXAxis name="xAxis" />
        *     <IgrNumericYAxis name="yAxis" />
        *
        *     <IgrColumnSeries
        *      name="series1"
        *     xAxisName="xAxis"
        * 	yAxisName="yAxis"
        * 	valueMemberPath="Value"
        * 	markerType = "circle"/>
        *  </IgrDataChart>
        * ```
        */
        get: function () {
            return this.i.ql;
        },
        set: function (v) {
            this.i.ql = ensureEnum(MarkerType_$type, v);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IgrMarkerSeries.prototype, "markerOutlineMode", {
        /**
         * Gets or sets whether the marker outline is based on the marker brush of the series rather than the marker outlines collection.
        */
        get: function () {
            return this.i.qi;
        },
        set: function (v) {
            this.i.qi = ensureEnum(MarkerOutlineMode_$type, v);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IgrMarkerSeries.prototype, "markerFillMode", {
        /**
         * Gets or sets whether the marker fill is based on the marker outline of the series rather than the marker brushes collection.
        */
        get: function () {
            return this.i.qe;
        },
        set: function (v) {
            this.i.qe = ensureEnum(MarkerFillMode_$type, v);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IgrMarkerSeries.prototype, "markerTemplate", {
        /**
         * Gets or sets the MarkerTemplate for the current series object.
        *
        * `MarkerTemplate` property gets or sets the MarkerTemplate for the current series object. For example, you can use it like this:
        */
        get: function () {
            return this.i.q7;
        },
        set: function (v) {
            this.i.q7 = v;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IgrMarkerSeries.prototype, "markerThickness", {
        /**
         * Gets or sets thickness of the marker outline
        */
        get: function () {
            return this.i.qq;
        },
        set: function (v) {
            this.i.qq = +v;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IgrMarkerSeries.prototype, "actualMarkerTemplate", {
        /**
         * Gets the effective marker template for the current series object.
        *
        * `ActualMarkerTemplate` property gets the effective marker template for the current series object. You can use it like this:
        */
        get: function () {
            return this.i.q5;
        },
        set: function (v) {
            this.i.q5 = v;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IgrMarkerSeries.prototype, "markerBrush", {
        /**
         * Gets or sets the brush that specifies how the current series object's marker interiors are painted.
        *
        * `MarkerBrush` property gets or sets the brush that specifies how the current series object's marker interiors are painted. You can use it like this:
        *
        * ```ts
        * this.series.markerBrush = "red";
        * ```
        *
        * ```ts
        *  <IgrDataChart
        *     dataSource={this.state.dataSource} >
        *
        *     <IgrCategoryXAxis name="xAxis" />
        *     <IgrNumericYAxis name="yAxis" />
        *
        *     <IgrColumnSeries
        *      name="series1"
        *     xAxisName="xAxis"
        * 	yAxisName="yAxis"
        * 	valueMemberPath="Value"
        * 	markerBrush = "blue" />
        *  </IgrDataChart>
        * ```
        */
        get: function () {
            return brushToString(this.i.rn);
        },
        set: function (v) {
            this.i.rn = stringToBrush(v);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IgrMarkerSeries.prototype, "actualMarkerBrush", {
        /**
         * Gets the effective marker brush for the current series object.
        *
        * `ActualMarkerBrush` property gets the effective marker brush for the current series object. For example, you can use it like this:
        *
        * ```ts
        *  let effectiveMarkerBrush: string = this.series.actualMarkerBrush;
        *
        * ```
        */
        get: function () {
            return brushToString(this.i.rl);
        },
        set: function (v) {
            this.i.rl = stringToBrush(v);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IgrMarkerSeries.prototype, "markerOutline", {
        /**
         * Gets or sets the brush that specifies how the current series object's marker outlines are painted.
        *
        * `MarkerOutline` property gets or sets the brush that specifies how the current series object's marker outlines are painted. You can use it like this:
        *
        * ```ts
        * this.series.markerOutline = "red";
        * ```
        *
        * ```ts
        *  <IgrDataChart
        *     dataSource={this.state.dataSource} >
        *
        *     <IgrCategoryXAxis name="xAxis" />
        *     <IgrNumericYAxis name="yAxis" />
        *
        *     <IgrColumnSeries
        *      name="series1"
        *     xAxisName="xAxis"
        * 	yAxisName="yAxis"
        * 	valueMemberPath="Value"
        * 	markerOutline = "blue" />
        *  </IgrDataChart>
        * ```
        */
        get: function () {
            return brushToString(this.i.ro);
        },
        set: function (v) {
            this.i.ro = stringToBrush(v);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IgrMarkerSeries.prototype, "actualMarkerOutline", {
        /**
         * Gets the effective marker outline for the current series object.
        *
        *     `ActualMarkerOutline` property gets the effective marker outline for the current series object. You can use it like this:
        *
        * ```ts
        *  let effectiveMarkerOutline: string = this.series.actualMarkerOutline;
        *
        * ```
        */
        get: function () {
            return brushToString(this.i.rm);
        },
        set: function (v) {
            this.i.rm = stringToBrush(v);
        },
        enumerable: false,
        configurable: true
    });
    return IgrMarkerSeries;
}(IgrSeries));
export { IgrMarkerSeries };
